import React, { useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { useAuth } from '../../context/AuthContext';
import {
  RefreshCw, Loader2, AlertCircle, Download, Image as ImageIcon, Play,
  TrendingUp, MousePointerClick, Eye, Receipt, Layers,
  Users, MapPin, Target,
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';

const API_URL = 'https://api.glowytics.com';
const HUPALUPA_LOGO = 'https://hupalupa.com.tr/images/logo_s.png';

const PLATFORMS = [
  // `color` = kart kenarlığı/aksan rengi (TikTok'ta marka siyahı). `bar` = dolu ilerleme
  // çubuğu rengi — siyah bar okunmaz/"bozuk" göründüğü için TikTok'a okunur marka pembesi verildi.
  { key: 'GOOGLE_ADS', label: 'Google Ads', base: '/api/google-ads', color: '#4285F4', bar: '#4285F4', soft: '#E8F0FE' },
  { key: 'META_ADS',   label: 'Meta Ads',   base: '/api/meta-ads',   color: '#0866FF', bar: '#0866FF', soft: '#E7F0FF' },
  { key: 'TIKTOK_ADS', label: 'TikTok Ads', base: '/api/tiktok-ads', color: '#111111', bar: '#FE2C55', soft: '#EDEDED' },
];

// Dönem = son 3 ay (90 günlük yuvarlanan pencere). TikTok reporting API geniş
// (yıllarca) pencereyi reddedip hata döndürdüğü için kısa pencere şart — yoksa
// TikTok slaytları hiç görünmez.
const WINDOW_DAYS = 90;
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const fmtInt = (n) => Number(n || 0).toLocaleString('tr-TR', { maximumFractionDigits: 0 });
const fmtMoney = (n) => Number(n || 0).toLocaleString('tr-TR', { maximumFractionDigits: 0 }) + ' ₺';
const fmtMoney2 = (n) => Number(n || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ₺';
const fmtNum = (n, d = 2) => Number(n || 0).toLocaleString('tr-TR', { minimumFractionDigits: d, maximumFractionDigits: d });
const fmtPct = (n) => fmtNum(n, 2) + '%';
const fmtCompact = (n) => {
  const v = Number(n || 0);
  if (v >= 1e6) return fmtNum(v / 1e6, 1) + 'M';
  if (v >= 1e3) return fmtNum(v / 1e3, 1) + 'B';
  return fmtInt(v);
};

// ── Platform marka logoları (inline SVG — ağ/CORS yok, ekran görüntüsü/PDF temiz) ──
function PlatformLogo({ pkey, size = 22 }) {
  // Google & Meta — sidebar/dashboard genelinde kullanılan standart asset'ler
  if (pkey === 'GOOGLE_ADS') {
    return (
      <img src="/google_ads-512.webp" alt="Google Ads" width={size} height={size}
        style={{ width: size, height: size, objectFit: 'contain', display: 'block' }} />
    );
  }
  if (pkey === 'META_ADS') {
    return (
      <img src="/6033716.png" alt="Meta Ads" width={size} height={size}
        style={{ width: size, height: size, objectFit: 'contain', display: 'block' }} />
    );
  }
  // TikTok — imzalı cyan/pink kayma efektiyle
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-label="TikTok" role="img">
      <path fill="#25F4EE" transform="translate(-0.6,0.4)" d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
      <path fill="#FE2C55" transform="translate(0.6,-0.4)" d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
      <path fill="#111111" d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
    </svg>
  );
}

// Kreatif kartı için gösterilecek metrikler (mevcut alanlara göre dinamik, en fazla 6).
// Reach sadece Meta'da; İzlenme/CPV/İzl.oranı sadece video (Google/TikTok) içerikte dolu.
function creativeMetrics(c) {
  const out = [
    { label: 'Gösterim', value: fmtCompact(c.impressions) },
    { label: 'CPM', value: fmtMoney(c.cpm) },   // CPM her platformda
  ];
  if (Number(c.reach) > 0)        out.push({ label: 'Erişim',  value: fmtCompact(c.reach) });
  if (Number(c.video_views) > 0)  out.push({ label: 'İzlenme', value: fmtCompact(c.video_views) });
  if (Number(c.cpv) > 0)          out.push({ label: 'CPV', value: fmtMoney2(c.cpv) });
  if (Number(c.view_rate) > 0)    out.push({ label: 'İzl. Oranı', value: fmtPct(c.view_rate) });
  out.push({ label: 'Tıklama', value: fmtCompact(c.clicks) });
  out.push({ label: 'CTR', value: fmtPct(c.ctr) });
  return out.slice(0, 6);
}

const chunk = (arr, n) => {
  const out = [];
  for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n));
  return out;
};

const TR_MONTHS = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

// "2026-05-01" → "1 Mayıs 2026" (Türkçe uzun tarih). Geçersiz/boş → null.
const fmtLongDate = (s) => {
  if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const [y, m, d] = s.split('-').map(Number);
  return `${d} ${TR_MONTHS[m - 1]} ${y}`;
};
// "2026-05-01" → "1 May" (grafik ekseni için kısa TR tarih)
const fmtDayShort = (s) => {
  if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const [, m, d] = s.split('-').map(Number);
  return `${d} ${TR_MONTHS[m - 1].slice(0, 3)}`;
};

// Aralığı takvim aylarına böler; ilk/son ay kırpılır (kısmi olabilir)
function monthsInRange(startStr, endStr) {
  const start = new Date(startStr), end = new Date(endStr);
  const out = [];
  let cur = new Date(start.getFullYear(), start.getMonth(), 1);
  const endMonth = new Date(end.getFullYear(), end.getMonth(), 1);
  while (cur <= endMonth) {
    const mStart = new Date(cur.getFullYear(), cur.getMonth(), 1);
    const mEnd = new Date(cur.getFullYear(), cur.getMonth() + 1, 0);
    const cs = mStart < start ? start : mStart;
    const ce = mEnd > end ? end : mEnd;
    const partial = cs.getDate() !== 1 || ce.getTime() !== mEnd.getTime();
    out.push({
      key: `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}`,
      label: TR_MONTHS[cur.getMonth()], year: cur.getFullYear(),
      start: ymd(cs), end: ymd(ce), partial,
    });
    cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
  }
  return out;
}

// Meta kampanya objective'i (API'den gelen ham değer) → okunur Türkçe etiket.
// Yeni ODAX (OUTCOME_*) ve eski (legacy) objective adlarının ikisi de kapsanır.
const META_OBJECTIVE_LABELS = {
  OUTCOME_AWARENESS: 'Bilinirlik', OUTCOME_ENGAGEMENT: 'Etkileşim', OUTCOME_TRAFFIC: 'Trafik',
  OUTCOME_LEADS: 'Potansiyel Müşteri', OUTCOME_SALES: 'Satış', OUTCOME_APP_PROMOTION: 'Uygulama Tanıtımı',
  BRAND_AWARENESS: 'Bilinirlik', REACH: 'Erişim', VIDEO_VIEWS: 'Video İzlenme',
  POST_ENGAGEMENT: 'Etkileşim', PAGE_LIKES: 'Sayfa Beğenisi', EVENT_RESPONSES: 'Etkinlik',
  LINK_CLICKS: 'Trafik', LANDING_PAGE_VIEWS: 'Trafik', MESSAGES: 'Mesaj',
  CONVERSIONS: 'Dönüşüm', PRODUCT_CATALOG_SALES: 'Katalog Satış', STORE_VISITS: 'Mağaza Ziyareti',
  LEAD_GENERATION: 'Potansiyel Müşteri', APP_INSTALLS: 'Uygulama Yüklemesi',
};
const metaObjectiveLabel = (obj) => {
  if (!obj) return null;
  return META_OBJECTIVE_LABELS[obj] || obj.replace(/^OUTCOME_/, '').replace(/_/g, ' ').toLowerCase()
    .replace(/\b\w/g, (m) => m.toUpperCase());
};

// Kampanya tipini türet. Meta'da GERÇEK objective (API'den) öncelikli; yoksa
// isimden tahmin. Google/TikTok isimden (objective alanı gelmiyor).
function deriveCampaignType(pkey, c) {
  const name = typeof c === 'string' ? c : (c?.campaign_name || '');
  const n = name.toLowerCase();
  if (pkey === 'GOOGLE_ADS') {
    if (/video|youtube|\byt\b|trueview|izlen/.test(n)) return 'YouTube / Video';
    if (/pmax|performance ?max|p-?max/.test(n)) return 'Performance Max';
    if (/search|arama|sem|brand|marka/.test(n)) return 'Arama (Search)';
    if (/demand ?gen|dgen|discovery/.test(n)) return 'Demand Gen';
    if (/display|gdn|banner/.test(n)) return 'Display';
    if (/view/.test(n)) return 'YouTube / Video';
    return 'Diğer';
  }
  if (pkey === 'META_ADS') {
    const objLabel = typeof c === 'object' ? metaObjectiveLabel(c?.objective) : null;
    if (objLabel) return objLabel;   // gerçek objective öncelikli
    if (/engagement|etkile/.test(n)) return 'Etkileşim';
    if (/reach|awareness|eriş|bilinir/.test(n)) return 'Erişim / Bilinirlik';
    if (/traffic|trafik|link/.test(n)) return 'Trafik';
    if (/conversion|satış|satis|sales|purchase|dönüş|donus/.test(n)) return 'Satış / Dönüşüm';
    if (/video|view|izlen/.test(n)) return 'Video İzlenme';
    if (/lead/.test(n)) return 'Potansiyel Müşteri';
    return 'Diğer';
  }
  // TIKTOK
  if (/engagement|etkile/.test(n)) return 'Etkileşim';
  if (/reach|eriş/.test(n)) return 'Erişim';
  if (/traffic|trafik/.test(n)) return 'Trafik';
  if (/conversion|satış|satis|dönüş/.test(n)) return 'Dönüşüm';
  if (/video|view|izlen/.test(n)) return 'Video İzlenme';
  return 'Diğer';
}

// Kreatif thumbnail URL'sini mutlak hale getir (Meta proxy göreli path döndürür)
const absThumb = (url) => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  return `${API_URL}${url.startsWith('/') ? '' : '/'}${url}`;
};

export default function HupalupaGoPresentation() {
  const { selectedBrand } = useAuth();
  const [nameContains, setNameContains] = useState('go');
  const [loading, setLoading] = useState(false);
  const [globalErr, setGlobalErr] = useState(null);
  const [insights, setInsights] = useState(null);   // { GOOGLE_ADS:{status,data}, ... }
  const [creatives, setCreatives] = useState(null);  // { GOOGLE_ADS:{status,data}, ... }
  const [monthly, setMonthly] = useState(null);      // [{ key,label,year,partial, byPlatform:{GOOGLE_ADS:spend,...}, total }]
  const [audiences, setAudiences] = useState(null);   // { status, data } — Google go audiences
  const [exporting, setExporting] = useState(false);
  const captureRef = useRef(null);

  const today = new Date();
  const untilStr = ymd(today);
  const sinceStr = ymd(new Date(today.getTime() - WINDOW_DAYS * 86400000));

  const load = useCallback(async () => {
    if (!selectedBrand?.id) { setGlobalErr('Önce bir marka seçin.'); return; }
    setLoading(true); setGlobalErr(null);
    const params = { dateRange: 'custom', startDate: sinceStr, endDate: untilStr, nameContains: nameContains.trim() };
    const headers = { 'X-Brand-ID': selectedBrand.id };

    const fetchAll = (path) => Promise.allSettled(
      PLATFORMS.map((p) => axios.get(`${API_URL}${p.base}${path}`, { params, headers, withCredentials: true, timeout: 180000 }))
    );

    // Not: TikTok kreatif küçük resimleri backend'de tiktok_item_id'den TikTok
    // oEmbed ile canlı çekiliyor (Spark Ads cover senkronu boş olduğu için).
    const [ins, crt] = await Promise.all([
      fetchAll('/live/campaign-insights'),
      fetchAll('/live/go-creatives'),
    ]);

    const mapResult = (settled) => {
      const next = {};
      settled.forEach((s, i) => {
        const key = PLATFORMS[i].key;
        if (s.status === 'fulfilled') {
          next[key] = { status: 'ok', data: s.value.data };
        } else {
          const code = s.reason?.response?.status;
          const msg = s.reason?.response?.data?.error || s.reason?.message;
          next[key] = { status: code === 404 ? 'noconn' : 'error', error: msg };
        }
      });
      return next;
    };

    setInsights(mapResult(ins));
    setCreatives(mapResult(crt));
    setLoading(false);
  }, [selectedBrand, nameContains, sinceStr, untilStr]);

  // Üç platformun go kampanyalarında kullandığı hedef kitle isimleri
  const loadAudiences = useCallback(async () => {
    if (!selectedBrand?.id) return;
    const headers = { 'X-Brand-ID': selectedBrand.id };
    const params = { nameContains: nameContains.trim(), startDate: sinceStr, endDate: untilStr };
    const settled = await Promise.allSettled(
      PLATFORMS.map((p) => axios.get(`${API_URL}${p.base}/live/go-audiences`, { params, headers, withCredentials: true, timeout: 180000 }))
    );
    const next = {};
    settled.forEach((s, i) => {
      const key = PLATFORMS[i].key;
      if (s.status === 'fulfilled') next[key] = { status: 'ok', data: s.value.data };
      else next[key] = { status: s.reason?.response?.status === 404 ? 'noconn' : 'error', error: s.reason?.response?.data?.error || s.reason?.message };
    });
    setAudiences(next);
  }, [selectedBrand, nameContains, sinceStr, untilStr]);

  // Aylık harcama — aralığı takvim aylarına bölüp her ay × platform için ayrı istek
  const loadMonthly = useCallback(async () => {
    if (!selectedBrand?.id) return;
    // Aylık Harcama Planı slaytı sadece Mayıs ve Haziran'ı gösterir (Nisan ve Temmuz hariç)
    const months = monthsInRange(sinceStr, untilStr).filter((m) => {
      const mm = m.key.slice(-2);
      return mm === '05' || mm === '06';
    });
    if (!months.length) return;
    const headers = { 'X-Brand-ID': selectedBrand.id };
    const nc = nameContains.trim();
    const blocks = await Promise.all(months.map(async (m) => {
      const params = { dateRange: 'custom', startDate: m.start, endDate: m.end, nameContains: nc };
      const settled = await Promise.allSettled(
        PLATFORMS.map((p) => axios.get(`${API_URL}${p.base}/live/campaign-insights`, { params, headers, withCredentials: true, timeout: 180000 }))
      );
      const byPlatform = {};
      let total = 0;
      settled.forEach((s, i) => {
        const spend = (s.status === 'fulfilled') ? Number(s.value.data?.totals?.spend || 0) : 0;
        byPlatform[PLATFORMS[i].key] = spend;
        total += spend;
      });
      return { ...m, byPlatform, total };
    }));
    setMonthly(blocks);
  }, [selectedBrand, nameContains, sinceStr, untilStr]);

  useEffect(() => { load(); loadMonthly(); loadAudiences(); }, [load, loadMonthly, loadAudiences]);

  // Birleşik toplamlar (campaign-insights)
  const combined = React.useMemo(() => {
    if (!insights) return null;
    const t = { spend: 0, impressions: 0, clicks: 0, reach: 0, video_views: 0, engagement: 0, conversions: 0, revenue: 0 };
    let any = false;
    for (const p of PLATFORMS) {
      const r = insights[p.key];
      if (r?.status === 'ok' && r.data?.totals) {
        any = true;
        const tot = r.data.totals;
        t.spend += Number(tot.spend || 0);
        t.impressions += Number(tot.impressions || 0);
        t.clicks += Number(tot.clicks || 0);
        t.reach += Number(tot.reach || 0);
        t.video_views += Number(tot.video_views || 0);
        t.engagement += Number(tot.engagement || 0);
        t.conversions += Number(tot.conversions || 0);
        t.revenue += Number(tot.revenue || 0);
      }
    }
    return any ? t : null;
  }, [insights]);

  const platformTotals = React.useMemo(() => {
    const out = {};
    if (!insights) return out;
    for (const p of PLATFORMS) {
      const r = insights[p.key];
      out[p.key] = (r?.status === 'ok' && r.data?.totals) ? r.data.totals : null;
    }
    return out;
  }, [insights]);

  // Seçili kampanyaların GERÇEK aktif aralığı — ilk gösterim (impression) alınan gün
  // → son gösterim alınan gün. Rapor penceresinden ve planlanan tarihten bağımsız,
  // fiilen yayında olunan aralık. Tüm platformlardaki go kampanyaları birleştirilir.
  const campaignPeriod = React.useMemo(() => {
    if (!insights) return null;
    let minStart = null, maxEnd = null;
    for (const p of PLATFORMS) {
      const r = insights[p.key];
      if (!(r?.status === 'ok' && Array.isArray(r.data?.campaigns))) continue;
      for (const c of r.data.campaigns) {
        const s = c.first_impression_date || null;
        const e = c.last_impression_date || null;
        if (s && (!minStart || s < minStart)) minStart = s;
        if (e && (!maxEnd || e > maxEnd)) maxEnd = e;
      }
    }
    if (!minStart && !maxEnd) return null;
    return { start: minStart, end: maxEnd };
  }, [insights]);

  // Günlük harcama serisi — her platformun `daily` dizisi tarihe göre birleştirilir.
  // Sonuç: [{ date, GOOGLE_ADS, META_ADS, TIKTOK_ADS, total }] tarih artan sıralı.
  const dailyCost = React.useMemo(() => {
    if (!insights) return null;
    const byDate = {};
    const activeKeys = [];
    for (const p of PLATFORMS) {
      const r = insights[p.key];
      const arr = (r?.status === 'ok' && Array.isArray(r.data?.daily)) ? r.data.daily : null;
      if (!arr || !arr.length) continue;
      activeKeys.push(p.key);
      for (const row of arr) {
        const d = row.date;
        if (!d) continue;
        const slot = (byDate[d] = byDate[d] || { date: d, total: 0 });
        const v = Number(row.spend || 0);
        slot[p.key] = (slot[p.key] || 0) + v;
        slot.total += v;
      }
    }
    const rows = Object.values(byDate).sort((a, b) => (a.date < b.date ? -1 : 1));
    if (!rows.length) return null;
    return { rows, activeKeys };
  }, [insights]);

  // Kampanya tipi kırılımı — platform başına, kampanya adından türetilmiş tip × harcama
  const typeBreakdown = React.useMemo(() => {
    const out = {};
    if (!insights) return out;
    for (const p of PLATFORMS) {
      const r = insights[p.key];
      if (!(r?.status === 'ok' && Array.isArray(r.data?.campaigns))) { out[p.key] = null; continue; }
      const byType = {};
      let total = 0;
      for (const c of r.data.campaigns) {
        const t = deriveCampaignType(p.key, c);
        const spend = Number(c.spend || 0);
        if (!byType[t]) byType[t] = { type: t, spend: 0, impressions: 0, count: 0 };
        byType[t].spend += spend;
        byType[t].impressions += Number(c.impressions || 0);
        byType[t].count += 1;
        total += spend;
      }
      const types = Object.values(byType).sort((a, b) => b.spend - a.spend)
        .map((x) => ({ ...x, share: total > 0 ? (x.spend / total) * 100 : 0 }));
      out[p.key] = { types, total };
    }
    return out;
  }, [insights]);

  // PDF export — tüm slaytları tek dosyaya
  const handleExportPDF = useCallback(async () => {
    if (exporting || !captureRef.current) return;
    setExporting(true);
    try {
      const slides = Array.from(captureRef.current.querySelectorAll('.slide'));
      if (!slides.length) return;
      // Slaytlar artık farklı yükseklikte olabilir (kreatif slaytları içeriğe göre
      // uzuyor) — her sayfayı kendi gerçek boyutunda ekle, ezme.
      let pdf = null;
      let added = 0, failed = 0;
      for (let i = 0; i < slides.length; i++) {
        try {
          const w = slides[i].offsetWidth || 1280;
          const h = slides[i].offsetHeight || 720;
          const canvas = await html2canvas(slides[i], {
            scale: 2, useCORS: true, backgroundColor: '#ffffff',
            logging: false, imageTimeout: 20000, windowWidth: w,
          });
          // Çapraz-kaynaklı kreatif görseller canvas'ı "taint" edebilir → toDataURL burada hata verir
          const imgData = canvas.toDataURL('image/jpeg', 0.92);
          const orient = w >= h ? 'landscape' : 'portrait';
          if (!pdf) pdf = new jsPDF({ orientation: orient, unit: 'px', format: [w, h] });
          else pdf.addPage([w, h], orient);
          pdf.addImage(imgData, 'JPEG', 0, 0, w, h);
          added++;
        } catch (slideErr) {
          failed++;
          console.warn(`Slayt ${i + 1} PDF'e eklenemedi (muhtemelen çapraz-kaynak görsel):`, slideErr?.message);
        }
      }
      if (added === 0 || !pdf) {
        alert('PDF oluşturulamadı (görseller çapraz-kaynak olduğu için). En sağlıklı yol: her slaytın ekran görüntüsünü almak.');
        return;
      }
      pdf.save(`Hupalupa_GO_Rapor_${(selectedBrand?.name || 'marka').replace(/[^\w-]+/g, '_')}.pdf`);
      if (failed > 0) alert(`PDF indirildi. ${failed} slayt çapraz-kaynak görsel nedeniyle atlandı — o slaytlar için ekran görüntüsü al.`);
    } catch (e) {
      console.error('PDF export failed', e);
      alert('PDF export sırasında hata oluştu. Konsolu kontrol et.');
    } finally {
      setExporting(false);
    }
  }, [exporting, selectedBrand]);

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      {/* Kontroller — capture dışında */}
      <div className="max-w-[1280px] mx-auto flex flex-wrap items-end gap-3 mb-5 print:hidden">
        <div className="flex items-center gap-2 mr-auto">
          <Layers className="w-6 h-6 text-fuchsia-600" />
          <div>
            <h1 className="text-xl font-bold text-gray-900">Hupalupa GO — Sunum Raporu</h1>
            <p className="text-xs text-gray-500">
              Google · Meta · TikTok — ismi "<strong>{nameContains || '…'}</strong>" geçen kampanyalar · son 3 ay
              {selectedBrand?.name ? ` · ${selectedBrand.name}` : ''}
            </p>
          </div>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Kampanya adı içerir</label>
          <input value={nameContains} onChange={(e) => setNameContains(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-28 outline-none focus:ring-2 focus:ring-fuchsia-500" placeholder="go" />
        </div>
        <button onClick={() => { load(); loadMonthly(); loadAudiences(); }} disabled={loading}
          className="flex items-center gap-2 bg-fuchsia-600 hover:bg-fuchsia-700 disabled:opacity-60 text-white font-semibold px-5 py-2 rounded-lg text-sm">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          {loading ? 'Çekiliyor…' : 'Yenile'}
        </button>
        <button onClick={handleExportPDF} disabled={exporting || loading}
          className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 text-white font-semibold px-5 py-2 rounded-lg text-sm">
          {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          {exporting ? 'PDF hazırlanıyor…' : 'Tümünü PDF indir'}
        </button>
      </div>

      {globalErr && (
        <div className="max-w-[1280px] mx-auto flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 mb-6 text-sm">
          <AlertCircle className="w-4 h-4 shrink-0" /> {globalErr}
        </div>
      )}

      {loading && !insights && (
        <div className="flex items-center gap-2 text-gray-500 text-sm py-20 justify-center">
          <Loader2 className="w-5 h-5 animate-spin" /> 3 platformdan canlı veri + kullanılan içerikler çekiliyor…
        </div>
      )}

      {/* SLAYTLAR */}
      <div ref={captureRef} className="flex flex-col items-center gap-8 overflow-x-auto">
        {/* her slayt PageBuilder ile sıralanır */}
        <SlideDeck
          insights={insights}
          creatives={creatives}
          combined={combined}
          platformTotals={platformTotals}
          monthly={monthly}
          typeBreakdown={typeBreakdown}
          audiences={audiences}
          nameContains={nameContains}
          brandName={selectedBrand?.name}
          brandId={selectedBrand?.id}
          period={{ start: sinceStr, end: untilStr }}
          campaignPeriod={campaignPeriod}
          dailyCost={dailyCost}
        />
      </div>
    </div>
  );
}

/* ====================================================================== */
/* Slayt deck — sıralama + numaralandırma                                  */
/* ====================================================================== */
function SlideDeck({ insights, creatives, combined, platformTotals, monthly, typeBreakdown, audiences, nameContains, brandName, brandId, period, campaignPeriod, dailyCost }) {
  if (!insights) return null;

  // Slaytları topla → sonra footer'a "n / toplam" yaz
  const built = [];

  // 1) Kapak
  built.push((key, num, total) => (
    <CoverSlide key={key} num={num} total={total} brandName={brandName} nameContains={nameContains} period={period} combined={combined} campaignPeriod={campaignPeriod} />
  ));

  // 2) Toplam performans
  if (combined) {
    built.push((key, num, total) => (
      <OverviewSlide key={key} num={num} total={total} combined={combined} platformTotals={platformTotals} brandName={brandName} period={period} />
    ));
    // 3) Aylık harcama (Platform × Ay) — klasik medya planı tarzı
    if (monthly && monthly.length > 0) {
      built.push((key, num, total) => (
        <MonthlySpendSlide key={key} num={num} total={total} monthly={monthly} brandName={brandName} />
      ));
    }
    // 3b) Günlük harcama trendi (line chart)
    if (dailyCost && dailyCost.rows.length > 0) {
      built.push((key, num, total) => (
        <DailyCostSlide key={key} num={num} total={total} dailyCost={dailyCost} brandName={brandName} />
      ));
    }
    // 4) Platform karşılaştırma
    built.push((key, num, total) => (
      <PlatformCompareSlide key={key} num={num} total={total} platformTotals={platformTotals} combined={combined} brandName={brandName} />
    ));
    // 5) Kampanya tipi kırılımı (YouTube / Engagement / Reach ...)
    const hasTypes = PLATFORMS.some((p) => typeBreakdown?.[p.key]?.types?.length);
    if (hasTypes) {
      built.push((key, num, total) => (
        <CampaignTypeSlide key={key} num={num} total={total} typeBreakdown={typeBreakdown} brandName={brandName} />
      ));
    }
  }

  // 4) Platform bazlı kampanya detayları
  for (const p of PLATFORMS) {
    const r = insights[p.key];
    if (r?.status === 'ok' && Array.isArray(r.data?.campaigns) && r.data.campaigns.length > 0) {
      const campaigns = [...r.data.campaigns].sort((a, b) => Number(b.spend || 0) - Number(a.spend || 0));
      const pages = chunk(campaigns, 9);
      pages.forEach((rows, idx) => {
        built.push((key, num, total) => (
          <CampaignSlide key={key} num={num} total={total} platform={p} rows={rows}
            totals={r.data.totals} pageIdx={idx} pageCount={pages.length} brandName={brandName} />
        ));
      });
    }
  }

  // 5) Kullanılan içerikler — platform bazlı, sayfalı
  for (const p of PLATFORMS) {
    const r = creatives?.[p.key];
    if (r?.status === 'ok' && Array.isArray(r.data?.creatives) && r.data.creatives.length > 0) {
      const items = [...r.data.creatives].sort((a, b) => Number(b.spend || 0) - Number(a.spend || 0));
      const pages = chunk(items, 8);
      pages.forEach((items4, idx) => {
        built.push((key, num, total) => (
          <CreativeSlide key={key} num={num} total={total} platform={p} items={items4}
            count={items.length} pageIdx={idx} pageCount={pages.length} brandId={brandId} />
        ));
      });
    }
  }

  // 6) Kullanılan hedef kitleler — her platform için ayrı slayt (toplu görünüm)
  for (const p of PLATFORMS) {
    const au = audiences?.[p.key];
    if (au?.status !== 'ok') continue;
    const d = au.data || {};
    if (Array.isArray(d.audiences) && d.audiences.length > 0) {
      const items = [...d.audiences];
      const pages = chunk(items, 45);
      pages.forEach((rows, idx) => {
        built.push((key, num, total) => (
          <AudiencesSlide key={key} num={num} total={total} platform={p}
            audiences={rows} totalCount={items.length} totalSpend={idx === 0 ? Number(d.total_spend || 0) : 0}
            pageIdx={idx} pageCount={pages.length} brandName={brandName} />
        ));
      });
    }
  }

  // 7) Kapanış
  built.push((key, num, total) => (
    <ClosingSlide key={key} num={num} total={total} combined={combined} platformTotals={platformTotals}
      insights={insights} creatives={creatives} brandName={brandName} />
  ));

  const total = built.length;
  return built.map((fn, i) => fn(`slide-${i}`, i + 1, total));
}

/* ====================================================================== */
/* Slayt çerçevesi (16:9 — 1280×720)                                       */
/* ====================================================================== */
function Slide({ num, total, kicker, title, subtitle, accent = '#C026D3', logoKey, children, footer, grow = false }) {
  return (
    <div className="slide relative bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden flex flex-col shrink-0"
      style={{ width: 1280, ...(grow ? { minHeight: 720 } : { height: 720 }) }}>
      <div className="h-1.5 w-full" style={{ background: accent }} />
      <header className="px-12 pt-7 pb-4 flex items-start justify-between gap-6">
        <div className="min-w-0 flex items-start gap-4">
          {logoKey && (
            <div className="w-14 h-14 rounded-2xl border border-gray-100 shadow-sm bg-white flex items-center justify-center shrink-0 mt-0.5">
              <PlatformLogo pkey={logoKey} size={34} />
            </div>
          )}
          <div className="min-w-0">
            {kicker && <div className="text-[12px] font-bold uppercase tracking-widest mb-1" style={{ color: accent }}>{kicker}</div>}
            <h2 className="text-3xl font-extrabold text-gray-900 leading-[1.35] py-0.5 whitespace-nowrap overflow-hidden text-ellipsis">{title}</h2>
            {subtitle && <p className="text-base text-gray-500 mt-1">{subtitle}</p>}
          </div>
        </div>
        <img src={HUPALUPA_LOGO} alt="Hupalupa" className="h-12 w-auto object-contain shrink-0" />
      </header>
      <div className={`flex-1 min-h-0 px-12 pb-3 ${grow ? '' : 'overflow-hidden'}`}>{children}</div>
      <footer className="px-12 py-3 border-t border-gray-100 flex items-center justify-between text-[12px] text-gray-400">
        <span className="font-semibold" style={{ color: accent }}>HUPALUPA GO · Çok Platform Reklam Raporu</span>
        <span>{footer || ''}</span>
        <span>{num} / {total}</span>
      </footer>
    </div>
  );
}

/* ---------------- KAPAK ---------------- */
function CoverSlide({ num, total, brandName, nameContains, period, combined, campaignPeriod }) {
  // İlk gösterim günü → son gösterim günü (kampanyaların fiilen yayında olduğu aralık).
  const cpStart = fmtLongDate(campaignPeriod?.start);
  const cpEnd = fmtLongDate(campaignPeriod?.end);
  const cpText = (cpStart && cpEnd)
    ? (cpStart === cpEnd ? cpStart : `${cpStart} → ${cpEnd}`)
    : (cpStart || cpEnd || null);
  return (
    <Slide num={num} total={total} kicker="" title="" accent="#C026D3"
      footer={brandName || ''}>
      <div className="h-full flex flex-col items-center justify-center text-center -mt-6">
        <img src={HUPALUPA_LOGO} alt="Hupalupa" className="h-20 w-auto object-contain mb-6" />
        <div className="text-[15px] font-bold uppercase tracking-[0.3em] text-fuchsia-600 mb-3">Çok Platform Reklam Raporu</div>
        <h1 className="text-6xl font-black text-gray-900 leading-none mb-4">HUPALUPA <span className="text-fuchsia-600">GO</span></h1>
        <p className="text-xl text-gray-500 max-w-3xl">
          Google · Meta · TikTok platformlarında yürütülen <strong className="text-gray-700">"{nameContains}"</strong> kampanyalarının
          performansı ve kullanılan içerikler
        </p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3 text-sm text-gray-500">
          {cpText && (
            <span className="px-4 py-1.5 rounded-full bg-fuchsia-600 text-white font-semibold shadow-sm">
              Kampanya dönemi: {cpText}
            </span>
          )}
          <span className="px-4 py-1.5 rounded-full bg-gray-100 border border-gray-200">Rapor penceresi: son 3 ay ({period.start} → {period.end})</span>
          {brandName && <span className="px-4 py-1.5 rounded-full bg-fuchsia-50 border border-fuchsia-100 text-fuchsia-700 font-semibold">{brandName}</span>}
        </div>
        {combined && (
          <div className="mt-10 grid grid-cols-3 gap-10">
            <CoverStat label="Toplam Harcama" value={fmtMoney(combined.spend)} />
            <CoverStat label="Toplam Gösterim" value={fmtCompact(combined.impressions)} />
            <CoverStat label="Toplam Erişim" value={combined.reach > 0 ? fmtCompact(combined.reach) : '—'} />
          </div>
        )}
      </div>
    </Slide>
  );
}
function CoverStat({ label, value }) {
  return (
    <div className="text-center">
      <div className="text-4xl font-black text-gray-900 tabular-nums">{value}</div>
      <div className="text-sm text-gray-400 mt-1 uppercase tracking-wide">{label}</div>
    </div>
  );
}

/* ---------------- TOPLAM PERFORMANS ---------------- */
function OverviewSlide({ num, total, combined, platformTotals, brandName, period }) {
  const shares = PLATFORMS.map((p) => ({
    p, spend: Number(platformTotals[p.key]?.spend || 0),
  }));
  const totalSpend = shares.reduce((s, x) => s + x.spend, 0) || 1;

  const kpis = [
    { label: 'Toplam Harcama', value: fmtMoney(combined.spend), icon: Receipt, color: '#059669', bg: '#ECFDF5' },
    { label: 'Gösterim', value: fmtInt(combined.impressions), icon: Eye, color: '#2563EB', bg: '#EFF6FF' },
    { label: 'Toplam Erişim', value: combined.reach > 0 ? fmtInt(combined.reach) : '—', icon: Users, color: '#DB2777', bg: '#FDF2F8' },
    { label: 'Tıklama', value: fmtInt(combined.clicks), icon: MousePointerClick, color: '#7C3AED', bg: '#F5F3FF' },
    { label: 'CTR', value: combined.impressions > 0 ? fmtPct((combined.clicks / combined.impressions) * 100) : '—', icon: TrendingUp, color: '#D97706', bg: '#FFFBEB' },
    { label: 'CPM', value: combined.impressions > 0 ? fmtMoney((combined.spend / combined.impressions) * 1000) : '—', icon: TrendingUp, color: '#0891B2', bg: '#ECFEFF' },
  ];

  return (
    <Slide num={num} total={total} kicker="Genel Bakış" title="Toplam Performans"
      subtitle="Google + Meta + TikTok — birleşik" accent="#059669" footer={brandName || ''}>
      <div className="h-full flex flex-col">
        <div className="grid grid-cols-3 gap-5">
          {kpis.map((k) => (
            <div key={k.label} className="rounded-2xl border p-5 flex items-center gap-4" style={{ borderColor: k.bg, background: k.bg }}>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ background: '#fff' }}>
                <k.icon className="w-6 h-6" style={{ color: k.color }} />
              </div>
              <div className="min-w-0">
                <div className="text-[12px] font-semibold uppercase tracking-wide" style={{ color: k.color }}>{k.label}</div>
                <div className="text-3xl font-black text-gray-900 tabular-nums leading-tight">{k.value}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-7">
          <div className="text-sm font-bold text-gray-600 uppercase tracking-wide mb-3">Platform Bazında Harcama Dağılımı</div>
          <div className="flex h-9 rounded-full overflow-hidden border border-gray-200">
            {shares.map(({ p, spend }) => {
              const pct = (spend / totalSpend) * 100;
              if (pct <= 0) return null;
              return (
                <div key={p.key} className="flex items-center justify-center text-[12px] font-bold text-white" style={{ width: `${pct}%`, background: p.bar || p.color }}
                  title={`${p.label} ${fmtNum(pct, 1)}%`}>
                  {pct >= 8 ? `${fmtNum(pct, 0)}%` : ''}
                </div>
              );
            })}
          </div>
          <div className="flex gap-6 mt-3">
            {shares.map(({ p, spend }) => (
              <div key={p.key} className="flex items-center gap-2 text-sm text-gray-600">
                <PlatformLogo pkey={p.key} size={18} />
                <span className="font-semibold">{p.label}</span>
                <span className="tabular-nums">{fmtMoney(spend)}</span>
                <span className="text-gray-400">({fmtNum((spend / totalSpend) * 100, 0)}%)</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Slide>
  );
}

/* ---------------- PLATFORM KARŞILAŞTIRMA ---------------- */
function PlatformCompareSlide({ num, total, platformTotals, combined, brandName }) {
  return (
    <Slide num={num} total={total} kicker="Karşılaştırma" title="Platform Karşılaştırması"
      subtitle="Her platformun katkısı" accent="#2563EB" footer={brandName || ''}>
      <div className="h-full grid grid-cols-3 gap-6">
        {PLATFORMS.map((p) => {
          const t = platformTotals[p.key];
          const has = !!t;
          return (
            <div key={p.key} className="rounded-2xl border-2 p-6 flex flex-col" style={{ borderColor: p.color }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl border border-gray-100 bg-white shadow-sm flex items-center justify-center shrink-0">
                  <PlatformLogo pkey={p.key} size={24} />
                </div>
                <h3 className="text-xl font-extrabold text-gray-900">{p.label}</h3>
              </div>
              {!has ? (
                <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">bağlantı / veri yok</div>
              ) : (
                <div className="flex-1 flex flex-col gap-3">
                  <CompareRow label="Harcama" value={fmtMoney(t.spend)} big />
                  <CompareRow label="Gösterim" value={fmtInt(t.impressions)} />
                  <CompareRow label="Erişim" value={Number(t.reach) > 0 ? fmtInt(t.reach) : '—'} />
                  <CompareRow label="İzlenme" value={Number(t.video_views) > 0 ? fmtInt(t.video_views) : '—'} />
                  <CompareRow label="Etkileşim" value={Number(t.engagement) > 0 ? fmtInt(t.engagement) : '—'} />
                  <CompareRow label="Tıklama" value={fmtInt(t.clicks)} />
                  <CompareRow label="CTR" value={t.impressions > 0 ? fmtPct((t.clicks / t.impressions) * 100) : '—'} />
                  <CompareRow label="CPM" value={t.impressions > 0 ? fmtMoney((t.spend / t.impressions) * 1000) : '—'} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Slide>
  );
}
function CompareRow({ label, value, big }) {
  return (
    <div className="flex items-center justify-between border-b border-gray-100 pb-2">
      <span className="text-sm text-gray-500">{label}</span>
      <span className={`tabular-nums font-bold text-gray-900 ${big ? 'text-2xl' : 'text-lg'}`}>{value}</span>
    </div>
  );
}

/* ---------------- AYLIK HARCAMA (Platform × Ay) — medya planı tarzı ---------------- */
function MonthlySpendSlide({ num, total, monthly, brandName }) {
  // Sadece harcaması olan platformları göster
  const activePlatforms = PLATFORMS.filter((p) => monthly.some((m) => Number(m.byPlatform[p.key] || 0) > 0));
  const platformGrand = {};
  activePlatforms.forEach((p) => { platformGrand[p.key] = monthly.reduce((s, m) => s + Number(m.byPlatform[p.key] || 0), 0); });
  const grandTotal = monthly.reduce((s, m) => s + m.total, 0);
  const monthMax = Math.max(1, ...monthly.map((m) => m.total));

  return (
    <Slide num={num} total={total} kicker="Başlangıç Özeti" title="Aylık Harcama Planı"
      subtitle="Platform × Ay — toplam reklam harcaması" accent="#059669" footer={brandName || ''}>
      <div className="h-full flex flex-col gap-4">
        <div className="rounded-2xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-gray-500 uppercase text-[11px]">
                <th className="text-left px-4 py-3 font-semibold">Platform</th>
                {monthly.map((m) => (
                  <th key={m.key} className="text-right px-4 py-3 font-semibold">
                    {m.label}{m.partial ? '*' : ''} <span className="text-gray-300 font-normal">'{String(m.year).slice(2)}</span>
                  </th>
                ))}
                <th className="text-right px-4 py-3 font-semibold bg-emerald-50 text-emerald-700">Toplam</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {activePlatforms.map((p) => (
                <tr key={p.key}>
                  <td className="px-4 py-3 font-semibold text-gray-800">
                    <span className="inline-flex items-center gap-2"><PlatformLogo pkey={p.key} size={20} />{p.label}</span>
                  </td>
                  {monthly.map((m) => (
                    <td key={m.key} className="px-4 py-3 text-right tabular-nums text-gray-700">{fmtMoney(m.byPlatform[p.key])}</td>
                  ))}
                  <td className="px-4 py-3 text-right tabular-nums font-bold bg-emerald-50/60 text-emerald-800">{fmtMoney(platformGrand[p.key])}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-gray-200 bg-gray-50 font-extrabold text-gray-900">
                <td className="px-4 py-3">TOPLAM</td>
                {monthly.map((m) => (
                  <td key={m.key} className="px-4 py-3 text-right tabular-nums">{fmtMoney(m.total)}</td>
                ))}
                <td className="px-4 py-3 text-right tabular-nums bg-emerald-100/70 text-emerald-800">{fmtMoney(grandTotal)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Aylık toplam — görsel bar */}
        <div className="flex-1 flex items-end justify-around gap-6 px-6 pb-1">
          {monthly.map((m) => (
            <div key={m.key} className="flex flex-col items-center justify-end flex-1 h-full">
              <div className="text-sm font-bold text-gray-800 tabular-nums mb-1">{fmtMoney(m.total)}</div>
              <div className="w-full max-w-[120px] rounded-t-lg bg-gradient-to-t from-emerald-500 to-emerald-400"
                style={{ height: `${Math.max(6, (m.total / monthMax) * 100)}%` }} />
              <div className="text-xs text-gray-500 mt-2 font-semibold">{m.label}{m.partial ? '*' : ''}</div>
            </div>
          ))}
        </div>
        <div className="text-[11px] text-gray-400">* kısmi ay (dönem aralığının ilk/son ayı tam değil)</div>
      </div>
    </Slide>
  );
}

/* ---------------- GÜNLÜK HARCAMA TRENDİ (line chart) ---------------- */
function DailyCostSlide({ num, total, dailyCost, brandName }) {
  const { rows, activeKeys } = dailyCost;
  const active = PLATFORMS.filter((p) => activeKeys.includes(p.key));
  const grand = rows.reduce((s, r) => s + Number(r.total || 0), 0);
  const peak = rows.reduce((mx, r) => (Number(r.total || 0) > Number(mx.total || 0) ? r : mx), rows[0]);
  const avg = rows.length ? grand / rows.length : 0;
  // Çok gün varsa eksende etiketleri seyrelt (okunur kalsın)
  const tickInterval = Math.max(0, Math.ceil(rows.length / 14) - 1);

  return (
    <Slide num={num} total={total} kicker="Başlangıç Özeti" title="Günlük Harcama Trendi"
      subtitle="Gün gün toplam reklam harcaması — platform kırılımlı" accent="#059669" footer={brandName || ''}>
      <div className="h-full flex flex-col">
        <div className="grid grid-cols-3 gap-5 mb-3">
          <TrendStat label="Toplam Harcama" value={fmtMoney(grand)} color="#059669" bg="#ECFDF5" />
          <TrendStat label="Günlük Ortalama" value={fmtMoney(avg)} color="#2563EB" bg="#EFF6FF" />
          <TrendStat label="En Yüksek Gün" value={`${fmtMoney(peak?.total)} · ${fmtDayShort(peak?.date)}`} color="#D97706" bg="#FFFBEB" />
        </div>
        <div className="flex-1 min-h-0 flex items-center justify-center">
          <LineChart width={1150} height={430} data={rows} margin={{ top: 10, right: 20, left: 10, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
            <XAxis dataKey="date" tickFormatter={fmtDayShort} tick={{ fontSize: 11, fill: '#6b7280' }}
              interval={tickInterval} tickMargin={8} minTickGap={16} />
            <YAxis tickFormatter={(v) => fmtCompact(v)} tick={{ fontSize: 11, fill: '#6b7280' }} width={64} />
            <Tooltip
              formatter={(v, name) => [fmtMoney(v), name]}
              labelFormatter={(d) => fmtLongDate(d) || d}
              contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line type="monotone" dataKey="total" name="Toplam" stroke="#059669" strokeWidth={3}
              dot={false} activeDot={{ r: 4 }} isAnimationActive={false} />
            {active.map((p) => (
              <Line key={p.key} type="monotone" dataKey={p.key} name={p.label}
                stroke={p.bar || p.color} strokeWidth={1.75} dot={false} activeDot={{ r: 3 }}
                connectNulls isAnimationActive={false} />
            ))}
          </LineChart>
        </div>
      </div>
    </Slide>
  );
}
function TrendStat({ label, value, color, bg }) {
  return (
    <div className="rounded-2xl border p-4 flex flex-col" style={{ borderColor: bg, background: bg }}>
      <div className="text-[12px] font-semibold uppercase tracking-wide" style={{ color }}>{label}</div>
      <div className="text-2xl font-black text-gray-900 tabular-nums leading-tight mt-0.5">{value}</div>
    </div>
  );
}

/* ---------------- KAMPANYA TİPİ KIRILIMI ---------------- */
function CampaignTypeSlide({ num, total, typeBreakdown, brandName }) {
  const active = PLATFORMS.filter((p) => typeBreakdown?.[p.key]?.types?.length);
  return (
    <Slide num={num} total={total} kicker="Başlangıç Özeti" title="Kampanya Tipi Kırılımı"
      subtitle="Her platformda hangi reklam tipine ne kadar harcandı" accent="#7C3AED" footer={brandName || ''}>
      <div className="h-full grid gap-6" style={{ gridTemplateColumns: `repeat(${active.length || 1}, minmax(0, 1fr))` }}>
        {active.map((p) => {
          const tb = typeBreakdown[p.key];
          return (
            <div key={p.key} className="rounded-2xl border-2 p-5 flex flex-col" style={{ borderColor: p.color }}>
              <div className="flex items-center gap-2.5 mb-1">
                <div className="w-9 h-9 rounded-xl border border-gray-100 bg-white shadow-sm flex items-center justify-center shrink-0">
                  <PlatformLogo pkey={p.key} size={22} />
                </div>
                <div>
                  <h3 className="text-lg font-extrabold text-gray-900 leading-none">{p.label}</h3>
                  <span className="text-[11px] text-gray-400">{fmtMoney(tb.total)} toplam</span>
                </div>
              </div>
              <div className="mt-3 flex flex-col gap-2.5">
                {tb.types.map((t) => (
                  <div key={t.type}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="font-semibold text-gray-700">{t.type} <span className="text-gray-300 font-normal">×{t.count}</span></span>
                      <span className="tabular-nums font-bold text-gray-900">{fmtMoney(t.spend)}</span>
                    </div>
                    <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${Math.max(3, t.share)}%`, background: p.bar || p.color }} />
                    </div>
                    <div className="text-[10px] text-gray-400 mt-0.5 text-right">{fmtNum(t.share, 0)}%</div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </Slide>
  );
}

/* ---------------- KAMPANYA DETAYI ---------------- */
function CampaignSlide({ num, total, platform, rows, totals, pageIdx, pageCount, brandName }) {
  const isGoogle = platform.key === 'GOOGLE_ADS';
  // Kolonlar platforma göre — Google'da İzlenme/İzl.Oranı/CPV; hiçbirinde Dönüşüm yok.
  const cols = isGoogle ? [
    { label: 'Harcama', bold: true, cell: (c) => fmtMoney(c.spend), foot: (t) => fmtMoney(t.spend) },
    { label: 'Gösterim', cell: (c) => fmtInt(c.impressions), foot: (t) => fmtInt(t.impressions) },
    { label: 'İzlenme', cell: (c) => fmtInt(c.video_views), foot: (t) => fmtInt(t.video_views) },
    { label: 'İzl. Oranı', cell: (c) => c.impressions > 0 ? fmtPct((Number(c.video_views || 0) / c.impressions) * 100) : '—', foot: (t) => t.impressions > 0 ? fmtPct((Number(t.video_views || 0) / t.impressions) * 100) : '—' },
    { label: 'CPV', cell: (c) => Number(c.video_views) > 0 ? fmtMoney2(c.spend / c.video_views) : '—', foot: (t) => Number(t.video_views) > 0 ? fmtMoney2(t.spend / t.video_views) : '—' },
    { label: 'CTR', cell: (c) => fmtPct(c.ctr), foot: (t) => t.impressions > 0 ? fmtPct((t.clicks / t.impressions) * 100) : '—' },
    { label: 'CPM', cell: (c) => fmtMoney(c.cpm), foot: () => '—' },
  ] : [
    { label: 'Harcama', bold: true, cell: (c) => fmtMoney(c.spend), foot: (t) => fmtMoney(t.spend) },
    { label: 'Gösterim', cell: (c) => fmtInt(c.impressions), foot: (t) => fmtInt(t.impressions) },
    ...(platform.key === 'META_ADS' ? [{ label: 'Erişim', cell: (c) => Number(c.reach) > 0 ? fmtInt(c.reach) : '—', foot: (t) => Number(t.reach) > 0 ? fmtInt(t.reach) : '—' }] : []),
    { label: 'Tıklama', cell: (c) => fmtInt(c.clicks), foot: (t) => fmtInt(t.clicks) },
    { label: 'CTR', cell: (c) => fmtPct(c.ctr), foot: (t) => t.impressions > 0 ? fmtPct((t.clicks / t.impressions) * 100) : '—' },
    { label: 'CPM', cell: (c) => fmtMoney(c.cpm), foot: () => '—' },
  ];
  return (
    <Slide num={num} total={total} kicker={`${platform.label} · Kampanyalar`}
      title={`${platform.label} Kampanya Detayı`}
      subtitle={pageCount > 1 ? `Sayfa ${pageIdx + 1}/${pageCount} · harcamaya göre` : 'Harcamaya göre sıralı'}
      accent={platform.color} logoKey={platform.key} footer={brandName || ''}>
      <div className="h-full flex flex-col">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-gray-400 uppercase text-[11px] border-b-2 border-gray-100">
              <th className="text-left py-2 font-semibold">Kampanya</th>
              {cols.map((col) => <th key={col.label} className="text-right py-2 font-semibold">{col.label}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {rows.map((c) => (
              <tr key={c.campaign_id || c.campaign_name}>
                <td className="py-2.5 pr-3 font-medium text-gray-800 max-w-[420px] truncate" title={c.campaign_name}>{c.campaign_name}</td>
                {cols.map((col) => (
                  <td key={col.label} className={`py-2.5 text-right tabular-nums ${col.bold ? 'font-semibold' : ''}`}>{col.cell(c)}</td>
                ))}
              </tr>
            ))}
          </tbody>
          {totals && pageIdx === pageCount - 1 && (
            <tfoot>
              <tr className="border-t-2 font-bold text-gray-900" style={{ borderColor: platform.color }}>
                <td className="py-2.5">TOPLAM ({platform.label})</td>
                {cols.map((col) => (
                  <td key={col.label} className="py-2.5 text-right tabular-nums">{col.foot(totals)}</td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </Slide>
  );
}

/* ---------------- KULLANILAN İÇERİKLER (kreatifler) ---------------- */
function CreativeSlide({ num, total, platform, items, count, pageIdx, pageCount, brandId }) {
  return (
    <Slide num={num} total={total} kicker={`${platform.label} · İçerikler`}
      title="Kullanılan İçerikler"
      subtitle={`${platform.label} · ${count} içerik${pageCount > 1 ? ` · sayfa ${pageIdx + 1}/${pageCount}` : ''}`}
      accent={platform.color} logoKey={platform.key} footer="" grow>
      <div className="grid grid-cols-4 gap-5 pb-2">
        {items.map((c) => (
          <CreativeCard key={c.id} c={c} platform={platform} brandId={brandId} />
        ))}
      </div>
    </Slide>
  );
}

function CreativeCard({ c, platform, brandId }) {
  const [errored, setErrored] = useState(false);
  let thumb = absThumb(c.thumbnail_url);
  // Meta proxy path'ine brandId ekle (header gönderilemez)
  if (thumb && /\/live\/creative-thumbnail/.test(thumb) && !/brandId=/.test(thumb)) {
    thumb += `${thumb.includes('?') ? '&' : '?'}brandId=${brandId}`;
  }
  const isVideo = c.type === 'video';
  const metrics = creativeMetrics(c);
  return (
    <div className="group relative rounded-2xl overflow-hidden flex flex-col bg-white ring-1 ring-gray-200 shadow-sm">
      {/* Görsel */}
      <div className="relative overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center" style={{ height: 240 }}>
        {thumb && !errored ? (
          <img src={thumb} alt={c.name || 'içerik'}
            className="w-full h-full object-cover" onError={() => setErrored(true)} />
        ) : (
          <div className="flex flex-col items-center text-gray-300">
            {isVideo ? <Play className="w-9 h-9" /> : <ImageIcon className="w-9 h-9" />}
            <span className="text-[10px] mt-1">önizleme yok</span>
          </div>
        )}
        {/* Alt gradient */}
        <div className="absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-black/45 to-transparent" />
        {/* Video oynat rozeti */}
        {isVideo && thumb && !errored && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-10 h-10 rounded-full bg-white/90 shadow-lg flex items-center justify-center">
              <Play className="w-5 h-5 text-gray-900 translate-x-[1px]" fill="currentColor" />
            </div>
          </div>
        )}
        {/* Tip etiketi */}
        <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-bold text-white flex items-center gap-1 shadow"
          style={{ background: platform.color }}>
          {isVideo ? <Play className="w-3 h-3" fill="currentColor" /> : <ImageIcon className="w-3 h-3" />}
          {isVideo ? 'Video' : (c.type === 'carousel' ? 'Karusel' : 'Görsel')}
        </span>
        {/* İzlenme oranı rozeti (video) */}
        {Number(c.view_rate) > 0 && (
          <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-bold text-gray-900 bg-white/90 shadow tabular-nums">
            %{fmtNum(c.view_rate, 0)} izl.
          </span>
        )}
      </div>
      {/* Gövde */}
      <div className="p-3 flex-1 flex flex-col">
        <div className="text-[12px] font-bold text-gray-800 leading-snug line-clamp-2" title={c.name}>{c.name || c.title || 'İçerik'}</div>
        {c.campaign_name && (
          <div className="text-[9px] text-gray-400 truncate mt-0.5" title={c.campaign_name}>{c.campaign_name}</div>
        )}
        <div className="mt-auto pt-2">
          <div className="flex items-center justify-between">
            <span className="text-[9px] uppercase tracking-wider text-gray-400 font-bold">Harcama</span>
            <span className="text-[15px] font-black tabular-nums" style={{ color: platform.color }}>{fmtMoney(c.spend)}</span>
          </div>
          <div className="grid grid-cols-3 gap-1.5 mt-2">
            {metrics.map((m) => (
              <div key={m.label} className="rounded-lg bg-gray-50 border border-gray-100 px-1 py-1 text-center">
                <div className="text-[8px] uppercase tracking-wide text-gray-400 font-bold truncate" title={m.label}>{m.label}</div>
                <div className="text-[10px] font-bold text-gray-800 tabular-nums truncate">{m.value}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- KULLANILAN HEDEF KİTLELER (platform bazlı) ---------------- */
const AUDIENCE_TYPE_META = {
  'Remarketing Kitlesi':     { icon: Users,  color: '#E11D48' },
  'Remarketing / Liste':     { icon: Users,  color: '#7C3AED' },
  'İlgi Alanı / In-Market':  { icon: Target, color: '#059669' },
  'İlgi Alanı':              { icon: Target, color: '#059669' },
  'Kitle (Combined)':        { icon: Layers, color: '#2563EB' },
  'Özel Kitle':              { icon: MapPin, color: '#DB2777' },
  'Davranış':                { icon: MousePointerClick, color: '#0891B2' },
  'Yaşam Olayı':             { icon: Layers, color: '#DB2777' },
};
const audienceTypeMeta = (t) => AUDIENCE_TYPE_META[t] || { icon: Users, color: '#6B7280' };
const AUDIENCE_TYPE_ORDER = ['Remarketing Kitlesi', 'Remarketing / Liste', 'Özel Kitle', 'Kitle (Combined)', 'İlgi Alanı / In-Market', 'İlgi Alanı', 'Davranış', 'Yaşam Olayı'];

const MEMBER_CHIP_COLOR = { KEYWORD: '#6B7280', URL: '#2563EB', APP: '#7C3AED', PLACE_CATEGORY: '#059669' };

// Custom audience (Google) — iç detaylı büyük kart (üye kelime/URL/app'leri)
function CustomAudienceCard({ a }) {
  const meta = audienceTypeMeta(a.type_label);
  const Icon = meta.icon;
  const members = a.detail?.members || [];
  const shown = members.slice(0, 40);
  const more = (a.detail?.member_count || members.length) - shown.length;
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-2.5 mb-2.5">
        <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${meta.color}18` }}>
          <Icon className="w-4 h-4" style={{ color: meta.color }} />
        </span>
        <div className="min-w-0">
          <div className="text-[16px] font-extrabold text-gray-900 leading-tight truncate" title={a.name}>{a.name}</div>
          <div className="text-[11px] text-gray-400">
            {a.detail?.subtype ? `${a.detail.subtype} · ` : ''}{a.detail?.member_count || members.length} üye · {a.campaign_count} kampanya
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {shown.map((m, i) => (
          <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium border"
            style={{ color: MEMBER_CHIP_COLOR[m.type] || '#6B7280', borderColor: `${MEMBER_CHIP_COLOR[m.type] || '#6B7280'}33`, background: `${MEMBER_CHIP_COLOR[m.type] || '#6B7280'}0d` }}>
            <span className="opacity-60">{m.type_label}</span>
            <span className="truncate max-w-[200px]" title={m.value}>{m.value}</span>
          </span>
        ))}
        {more > 0 && <span className="text-[11px] text-gray-400 self-center font-semibold">+{more} daha</span>}
      </div>
    </div>
  );
}

// Toplu hedef kitle görünümü — tipe göre gruplu, büyük tag'ler. Per-item harcama YOK
// (Meta interest'e bölünemez → toplam bir kez, doğru gösterilir).
function AudiencesSlide({ num, total, platform, audiences, totalCount, totalSpend, pageIdx, pageCount, brandName }) {
  const groups = {};
  for (const a of audiences) (groups[a.type_label] = groups[a.type_label] || []).push(a);
  const groupKeys = Object.keys(groups).sort((a, b) => {
    const ia = AUDIENCE_TYPE_ORDER.indexOf(a), ib = AUDIENCE_TYPE_ORDER.indexOf(b);
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  });
  const nTypes = groupKeys.length;

  return (
    <Slide num={num} total={total} kicker={`${platform.label} · Hedef Kitle`} title="Kullanılan Hedef Kitleler"
      subtitle={`${platform.label} · ${totalCount} kitle · ${nTypes} tür${pageCount > 1 ? ` · sayfa ${pageIdx + 1}/${pageCount}` : ''}`}
      accent={platform.color} logoKey={platform.key} footer={brandName || ''} grow>
      {totalSpend > 0 && (
        <div className="flex items-center justify-end mb-3">
          <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-[14px] font-bold text-white" style={{ background: platform.color }}>
            <Receipt className="w-4 h-4" /> Toplam Harcama {fmtMoney(totalSpend)}
          </span>
        </div>
      )}

      <div className="flex flex-col gap-4 pb-2">
        {groupKeys.map((gk) => {
          const meta = audienceTypeMeta(gk);
          const Icon = meta.icon;
          const items = groups[gk];
          const hasMembers = items.some((a) => (a.detail?.members || []).length > 0);
          return (
            <div key={gk}>
              <div className="flex items-center gap-2 mb-2.5">
                <span className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${meta.color}18` }}>
                  <Icon className="w-4 h-4" style={{ color: meta.color }} />
                </span>
                <h3 className="text-[17px] font-extrabold text-gray-900">{gk}</h3>
                <span className="text-[12px] font-bold px-2.5 py-0.5 rounded-full" style={{ background: `${meta.color}14`, color: meta.color }}>{items.length}</span>
              </div>
              {hasMembers ? (
                <div className="grid grid-cols-2 gap-3">
                  {items.map((a, i) => <CustomAudienceCard key={`${a.name}-${i}`} a={a} />)}
                </div>
              ) : items.some((a) => a.subtitle) ? (
                <div className="grid grid-cols-3 gap-3">
                  {items.map((a, i) => (
                    <div key={`${a.name}-${i}`} className="rounded-2xl border bg-white p-3.5 flex items-center gap-3 shadow-sm"
                      style={{ borderColor: `${meta.color}2e` }}>
                      <span className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${meta.color}18` }}>
                        <Icon className="w-5 h-5" style={{ color: meta.color }} />
                      </span>
                      <div className="min-w-0">
                        <div className="text-[14px] font-bold text-gray-900 leading-tight truncate" title={a.name}>{a.name}</div>
                        {a.subtitle && <div className="text-[11px] mt-0.5 font-semibold" style={{ color: meta.color }}>{a.subtitle}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {items.map((a, i) => (
                    <span key={`${a.name}-${i}`} className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border text-[14px] font-semibold"
                      style={{ borderColor: `${meta.color}2e`, background: `${meta.color}0d`, color: '#1f2937' }} title={a.name}>
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ background: meta.color }} />
                      {a.name}
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Slide>
  );
}

/* ---------------- KAPANIŞ ---------------- */
function ClosingSlide({ num, total, combined, platformTotals, insights, creatives, brandName }) {
  const platLines = PLATFORMS.map((p) => {
    const t = platformTotals[p.key];
    const crt = creatives?.[p.key];
    const nCreatives = (crt?.status === 'ok' && Array.isArray(crt.data?.creatives)) ? crt.data.creatives.length : 0;
    const nCamp = (insights?.[p.key]?.status === 'ok' && Array.isArray(insights[p.key].data?.campaigns)) ? insights[p.key].data.campaigns.length : 0;
    return { p, t, nCreatives, nCamp };
  });

  const cTot = combined || {};
  const cCtr = cTot.impressions > 0 ? (cTot.clicks / cTot.impressions) * 100 : 0;
  const cCpm = cTot.impressions > 0 ? (cTot.spend / cTot.impressions) * 1000 : 0;
  const cViewRate = cTot.impressions > 0 ? (Number(cTot.video_views || 0) / cTot.impressions) * 100 : 0;
  const cFreq = cTot.reach > 0 ? cTot.impressions / cTot.reach : 0;
  const cCpv = Number(cTot.video_views) > 0 ? cTot.spend / cTot.video_views : 0;

  return (
    <Slide num={num} total={total} kicker="Özet" title="Genel Değerlendirme" accent="#C026D3" footer={brandName || ''}>
      <div className="h-full flex flex-col gap-3.5">
        {combined && (
          <>
            {/* Ana KPI bandı — 6 metrik */}
            <div className="grid grid-cols-6 gap-3">
              <SummaryStat label="Harcama" value={fmtMoney(combined.spend)} accent="#059669" />
              <SummaryStat label="Gösterim" value={fmtCompact(combined.impressions)} accent="#2563EB" />
              <SummaryStat label="Erişim" value={combined.reach > 0 ? fmtCompact(combined.reach) : '—'} accent="#DB2777" />
              <SummaryStat label="İzlenme" value={combined.video_views > 0 ? fmtCompact(combined.video_views) : '—'} accent="#DC2626" />
              <SummaryStat label="Etkileşim" value={combined.engagement > 0 ? fmtCompact(combined.engagement) : '—'} accent="#D97706" />
              <SummaryStat label="Tıklama" value={fmtCompact(combined.clicks)} accent="#7C3AED" />
            </div>
            {/* Türetilmiş oranlar */}
            <div className="grid grid-cols-5 gap-3">
              <MiniStat label="CTR" value={fmtPct(cCtr)} />
              <MiniStat label="CPM" value={fmtMoney(cCpm)} />
              <MiniStat label="CPV" value={cCpv > 0 ? fmtMoney2(cCpv) : '—'} />
              <MiniStat label="İzlenme Oranı" value={cViewRate > 0 ? fmtPct(cViewRate) : '—'} />
              <MiniStat label="Frekans" value={cFreq > 0 ? fmtNum(cFreq, 1) : '—'} />
            </div>
          </>
        )}
        <div className="flex-1 rounded-2xl border border-gray-200 overflow-hidden">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="bg-gray-50 text-gray-400 uppercase text-[10px]">
                <th className="text-left px-3 py-2 font-semibold">Platform</th>
                <th className="text-right px-3 py-2 font-semibold">Kamp.</th>
                <th className="text-right px-3 py-2 font-semibold">İçerik</th>
                <th className="text-right px-3 py-2 font-semibold">Harcama</th>
                <th className="text-right px-3 py-2 font-semibold">Gösterim</th>
                <th className="text-right px-3 py-2 font-semibold">Erişim</th>
                <th className="text-right px-3 py-2 font-semibold">İzlenme</th>
                <th className="text-right px-3 py-2 font-semibold">Etkileşim</th>
                <th className="text-right px-3 py-2 font-semibold">Tıklama</th>
                <th className="text-right px-3 py-2 font-semibold">CTR</th>
                <th className="text-right px-3 py-2 font-semibold">CPM</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {platLines.map(({ p, t, nCreatives, nCamp }) => (
                <tr key={p.key}>
                  <td className="px-3 py-2.5 font-semibold text-gray-800">
                    <span className="inline-flex items-center gap-2"><PlatformLogo pkey={p.key} size={18} />{p.label}</span>
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-gray-500">{nCamp}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-gray-500">{nCreatives}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-semibold">{t ? fmtMoney(t.spend) : '—'}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{t ? fmtInt(t.impressions) : '—'}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{t && Number(t.reach) > 0 ? fmtInt(t.reach) : '—'}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{t && Number(t.video_views) > 0 ? fmtInt(t.video_views) : '—'}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{t && Number(t.engagement) > 0 ? fmtInt(t.engagement) : '—'}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{t ? fmtInt(t.clicks) : '—'}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{t && t.impressions > 0 ? fmtPct((t.clicks / t.impressions) * 100) : '—'}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{t && t.impressions > 0 ? fmtMoney((t.spend / t.impressions) * 1000) : '—'}</td>
                </tr>
              ))}
            </tbody>
            {combined && (
              <tfoot>
                <tr className="border-t-2 border-fuchsia-100 bg-fuchsia-50/40 font-bold text-gray-900">
                  <td className="px-3 py-2.5">TOPLAM</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{platLines.reduce((s, x) => s + x.nCamp, 0)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{platLines.reduce((s, x) => s + x.nCreatives, 0)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{fmtMoney(combined.spend)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{fmtInt(combined.impressions)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{combined.reach > 0 ? fmtInt(combined.reach) : '—'}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{combined.video_views > 0 ? fmtInt(combined.video_views) : '—'}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{combined.engagement > 0 ? fmtInt(combined.engagement) : '—'}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{fmtInt(combined.clicks)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{fmtPct(cCtr)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{fmtMoney(cCpm)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        <div className="text-center text-[12px] text-gray-400">Veriler ilgili reklam platformlarının API'lerinden canlı çekilmiştir · Glowytics</div>
      </div>
    </Slide>
  );
}
function SummaryStat({ label, value, accent }) {
  return (
    <div className="rounded-2xl border border-gray-200 p-3.5 text-center">
      <div className="text-[26px] font-black tabular-nums leading-tight" style={{ color: accent }}>{value}</div>
      <div className="text-[11px] text-gray-400 uppercase tracking-wide mt-0.5">{label}</div>
    </div>
  );
}
function MiniStat({ label, value }) {
  return (
    <div className="rounded-xl bg-gray-50 border border-gray-100 px-3 py-2 flex items-center justify-between">
      <span className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">{label}</span>
      <span className="text-[15px] font-bold text-gray-800 tabular-nums">{value}</span>
    </div>
  );
}
